import React, { useMemo } from 'react';

export default function StudentProfileModal({ isOpen, onClose, studentId, studentName, allData, subjectColorMap, getChapterName }) {
  const studentData = useMemo(() => {
    if (!isOpen || !studentId) return [];
    return (allData || []).filter(d => d.penId === studentId && d.minutesUsed > 0)
      .sort((a, b) => (b.dayStartS || 0) - (a.dayStartS || 0));
  }, [allData, studentId, isOpen]);

  const stats = useMemo(() => {
    let total = 0;
    const subjects = {};
    studentData.forEach(d => {
      total += d.minutesUsed || 0;
      if (d.subject) {
        subjects[d.subject] = (subjects[d.subject] || 0) + (d.minutesUsed || 0);
      }
    });
    const topSubj = Object.entries(subjects).sort((a,b) => b[1] - a[1])[0];
    
    return {
      totalMin: total,
      topSubject: topSubj ? topSubj[0] : 'None',
      subjectsCount: Object.keys(subjects).length
    };
  }, [studentData]);

  if (!isOpen || !studentId) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl rounded-3xl border border-slate-800 bg-slate-900/90 shadow-2xl p-6 md:p-8 flex flex-col max-h-[90vh]">
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 text-slate-400 hover:text-white bg-slate-800/50 p-2 rounded-full transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        <div className="flex items-center gap-4 mb-8">
          <div className="flex shrink-0 h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-400 text-2xl font-bold text-slate-950 shadow-[0_4px_20px_rgba(99,102,241,0.3)]">
            {(studentName || studentId || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold tracking-tight text-white truncate">{studentName}</h2>
            <p className="text-sm text-slate-400 font-mono mt-1 truncate">ID: {studentId}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8 shrink-0">
          <div className="rounded-2xl bg-slate-950/50 border border-slate-800/50 p-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Total Practice</p>
            <p className="mt-1 text-2xl font-semibold text-slate-100 tabular-nums">{stats.totalMin}<span className="text-sm font-normal text-slate-500 ml-1">min</span></p>
          </div>
          <div className="rounded-2xl bg-slate-950/50 border border-slate-800/50 p-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Top Subject</p>
            <p className="mt-1 text-lg font-semibold text-indigo-300 truncate">{stats.topSubject}</p>
          </div>
          <div className="rounded-2xl bg-slate-950/50 border border-slate-800/50 p-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Subjects</p>
            <p className="mt-1 text-2xl font-semibold text-slate-100 tabular-nums">{stats.subjectsCount}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 rounded-xl border border-slate-800/50 bg-slate-950/30 scrollbar-thin scrollbar-thumb-slate-700">
          <div className="sticky top-0 bg-slate-950/95 backdrop-blur px-5 py-3 border-b border-slate-800 z-10 flex justify-between items-center">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-300">Session History</h3>
            <span className="text-[10px] font-medium bg-slate-800/80 px-2 py-0.5 rounded text-slate-400">Latest 50</span>
          </div>
          {studentData.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500 flex flex-col items-center gap-3">
              <span className="text-3xl">📭</span>
              No sessions recorded yet.
            </div>
          ) : (
            <div className="divide-y divide-slate-800/50">
              {studentData.slice(0, 50).map((session, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-800/30 transition-colors">
                  <div className="flex items-center gap-3.5">
                    <div className="h-2.5 w-2.5 rounded-full shadow-[0_0_8px_currentColor]" style={{ backgroundColor: subjectColorMap[session.subject] || '#6366f1', color: subjectColorMap[session.subject] || '#6366f1' }} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-200 truncate">{session.subject}</p>
                      <p className="text-[11px] text-slate-500 truncate" title={session.chapter !== "Uncategorized" && getChapterName ? getChapterName(session.subject, session.chapter) : 'General Practice'}>
                        {session.chapter !== "Uncategorized" && getChapterName ? getChapterName(session.subject, session.chapter) : 'General Practice'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-emerald-400 tabular-nums">+{session.minutesUsed} min</p>
                    <p className="text-[10px] text-slate-500 tabular-nums">{new Date((session.dayStartS || 0) * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric'})}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
